import { Injectable } from "@angular/core";
import { DataAccessService } from "@kells/apis/data-access";
import { EMPTY } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";
import { AnalyticsService } from "@kells/apis/analytics";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "apps/patient-reporting/src/environments/environment";
import {
  UpgradePlanModalComponent,
  UpgradePlanModalData,
} from "../containers/upgrade-plan";
import {
  CloseConfirmUpgradePlanModal,
  CloseUpgradePlanModal,
  OpenConfirmUpgradePlanModal,
  OpenUpgradePlanModal,
} from "apps/patient-reporting/src/app/shared/models/analytics/home-events";
import { ConfirmationModalComponent } from "../components/confirmation-modal/confirmation-modal.component";
import { Product } from "@kells/interfaces/patient";

@Injectable({
  providedIn: "root",
})
export class UpgradePlanService {
  public readonly isProviderNetworkAvailable$ = this.data.hiddenData$.pipe(
    map((hiddenData) => !hiddenData.providerNetwork)
  );

  constructor(
    private readonly data: DataAccessService,
    private readonly analyticsService: AnalyticsService,
    private readonly dialog: MatDialog
  ) {}

  getPlanProduct(
    products: Product[],
    productId: string,
    recurringCycle: "year" | "month"
  ) {
    return products.find(
      (product) =>
        product.product_id === productId &&
        product.recurring_cycle === recurringCycle
    );
  }

  getCompletePlanProduct(
    products: Product[],
    recurringCycle: "year" | "month"
  ) {
    return this.getPlanProduct(
      products,
      environment.stripeCompletePlanProductId,
      recurringCycle
    );
  }

  getPremiumPlanProduct(products: Product[], recurringCycle: "year" | "month") {
    return this.getPlanProduct(
      products,
      environment.stripePremiumPlanProductId,
      recurringCycle
    );
  }

  openUpgradePlanModal(options?: UpgradePlanModalData) {
    return this.data.selectedPatientCanManagePlan$.pipe(
      take(1),
      switchMap((canManagePlan) => {
        if (!canManagePlan) {
          return EMPTY;
        }

        this.analyticsService.record(OpenUpgradePlanModal({}));

        return this.isProviderNetworkAvailable$.pipe(
          take(1),
          switchMap((isProviderNetworkAvailable) =>
            this.dialog
              .open(UpgradePlanModalComponent, {
                autoFocus: false,
                panelClass: ["reports-modal"],
                width: isProviderNetworkAvailable ? "1250px" : "920px",
                maxWidth: "100%",
                data: options,
              })
              .afterClosed()
              .pipe(
                tap(() =>
                  this.analyticsService.record(CloseUpgradePlanModal({}))
                )
              )
          )
        );
      })
    );
  }

  openConfirmUpgradePlanModal() {
    this.analyticsService.record(OpenConfirmUpgradePlanModal({}));

    return this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: "You need to upgrade your plan to unlock this service",
          requireConfirmation: true,
          okText: "Upgrade",
        },
        autoFocus: false,
        panelClass: ["reports-modal"],
      })
      .afterClosed()
      .pipe(
        tap((confirmed) => {
          this.analyticsService.record(CloseConfirmUpgradePlanModal({}));

          if (!confirmed) return;

          return this.openUpgradePlanModal();
        })
      );
  }
}
